// src/shared/components/layout/Sidebar.tsx

import React from 'react';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  children?: MenuItem[];
  disabled?: boolean;
}

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: '🏠',
    path: '/'
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: '📊',
    disabled: true
  },
  {
    id: 'customers',
    label: 'Clientes',
    icon: '👥',
    disabled: true,
    children: [
      { id: 'customers-list', label: 'Lista', icon: '📋' },
      { id: 'customers-add', label: 'Agregar', icon: '➕' }
    ]
  },
  {
    id: 'sales',
    label: 'Ventas',
    icon: '💰',
    disabled: true,
    children: [
      { id: 'sales-overview', label: 'Resumen', icon: '📈' },
      { id: 'sales-invoices', label: 'Facturas', icon: '🧾' },
      { id: 'sales-reports', label: 'Reportes', icon: '📊' }
    ]
  },
  {
    id: 'inventory',
    label: 'Inventario',
    icon: '📦',
    disabled: true
  },
  {
    id: 'settings',
    label: 'Configuración',
    icon: '⚙️',
    disabled: true
  }
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set());

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.disabled) {
      return;
    }

    if (item.children) {
      toggleExpanded(item.id);
    } else {
      // Aquí iría la navegación cuando se implemente el router
      console.log(`Navegando a: ${item.path || item.id}`);
      // En móvil, cerrar sidebar después de navegar
      if (window.innerWidth <= 768) {
        onClose();
      }
    }
  };

  const renderMenuItem = (item: MenuItem, level = 0) => {
    const isExpanded = expandedItems.has(item.id);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <div key={item.id} className="menu-item-container">
        <button
          className={`menu-item ${level > 0 ? 'menu-item--child' : ''} ${item.disabled ? 'menu-item--disabled' : ''}`}
          onClick={() => handleItemClick(item)}
          disabled={item.disabled}
          style={{ paddingLeft: `${1 + level * 0.5}rem` }}
        >
          <span className="menu-icon">{item.icon}</span>
          <span className="menu-label">{item.label}</span>
          {hasChildren && (
            <span className={`menu-arrow ${isExpanded ? 'expanded' : ''}`}>
              ▼
            </span>
          )}
          {item.disabled && (
            <span className="menu-badge">Próximamente</span>
          )}
        </button>

        {hasChildren && isExpanded && (
          <div className="menu-children">
            {item.children!.map(child => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <aside className={`sidebar ${isOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar-content">
          {/* Header del sidebar */}
          <div className="sidebar-header">
            <h3 className="sidebar-title">Menú Principal</h3>
            <button 
              className="sidebar-close"
              onClick={onClose}
              aria-label="Cerrar menú"
            >
              ✕
            </button>
          </div>

          {/* Navegación */}
          <nav className="sidebar-nav">
            <div className="menu-section">
              <div className="menu-section-title">General</div>
              {menuItems.slice(0, 2).map(item => renderMenuItem(item))}
            </div>

            <div className="menu-section">
              <div className="menu-section-title">Gestión</div>
              {menuItems.slice(2, -1).map(item => renderMenuItem(item))}
            </div>

            <div className="menu-section">
              <div className="menu-section-title">Sistema</div>
              {menuItems.slice(-1).map(item => renderMenuItem(item))}
            </div>
          </nav>

          {/* Footer del sidebar */}
          <div className="sidebar-footer">
            <div className="sidebar-version">
              <small>iBloom v1.0.0</small>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;